import { inject, Injectable } from '@angular/core';
import { PreferencesService } from 'src/app/services/preferences.service';
import { environment } from 'src/environments/environment';

export interface FeatureFlagStorage {
	flag: string;
	value: boolean;
}

export interface FeatureFlag {
	name: string;
	defaultValue: boolean;
	currentValue: boolean;
}

@Injectable({
	providedIn: 'root',
})
export class FeatureFlagService {
	protected readonly features: { [key: string]: boolean } = environment.feature;
	protected readonly preferencesService = inject(PreferencesService);

	constructor() {}

	public getFeatureFlags(): FeatureFlag[] {
		const featureFlags = Object.entries(this.features).map(([key, value]) => ({
			name: key,
			defaultValue: value,
			currentValue: value,
		}));

		const overrides = this.preferencesService.getPreference<FeatureFlagStorage[]>('featureFlags');

		for (const override of overrides || []) {
			const flag = featureFlags.find((f) => f.name === override.flag);
			if (flag) {
				flag.currentValue = override.value;
			}
		}

		return featureFlags;
	}

	public overrideFlag(flag: FeatureFlag): void {
		const localStorageFlag: FeatureFlagStorage = { flag: flag.name, value: flag.currentValue };
		const existingFeatureFlags = this.preferencesService.getPreference<FeatureFlagStorage[]>('featureFlags') || [];
		const filteredFlags = existingFeatureFlags.filter((f) => f.flag !== flag.name);

		let updatedFlags = filteredFlags;
		if (flag.currentValue !== flag.defaultValue) {
			updatedFlags = [...filteredFlags, localStorageFlag];
		}

		this.preferencesService.setPreference({ featureFlags: updatedFlags });
	}

	public getFeatureFlagState(flag: keyof typeof environment.feature): boolean {
		const featureFlags = this.getFeatureFlags();
		const featureFlag = featureFlags.find((f) => f.name === flag);
		return featureFlag ? featureFlag.currentValue : this.features[flag];
	}
}
